import { useEffect, useState } from 'react';
import { Loader2, TicketPercent } from 'lucide-react';
import { AppliedCouponPill } from '@/features/campaigns/components/AppliedCouponPill';
import { formatMoney } from '@/shared/lib/format/money';
import type { PreviewCouponControls } from '../PreviewStep.types';

type PendingAction = {
  type: 'apply' | 'remove';
  code: string;
  source: 'manual' | 'offer' | 'remove';
};

export function CouponSection({
  applied,
  coupons,
  isLoading,
  isBusy,
  onApply,
  onRemove,
}: PreviewCouponControls) {
  const [code, setCode] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [showAllOffers, setShowAllOffers] = useState(false);
  const trimmed = code.trim();
  const appliedCodes = new Set(applied.map((item) => item.code.toUpperCase()));

  useEffect(() => {
    if (!isBusy) setPending(null);
  }, [isBusy]);

  const submit = (next: string, source: 'manual' | 'offer') => {
    const value = next.trim();
    if (!value || isBusy || appliedCodes.has(value.toUpperCase())) return;
    setPending({ type: 'apply', code: value, source });
    onApply(value);
    if (source === 'manual') setCode('');
  };

  const isApplying = (couponCode: string, source: 'manual' | 'offer') =>
    Boolean(
      isBusy &&
        pending?.type === 'apply' &&
        pending.source === source &&
        pending.code.toUpperCase() === couponCode.toUpperCase(),
    );

  const isRemoving = (couponCode: string) =>
    Boolean(
      isBusy &&
        pending?.type === 'remove' &&
        pending.code.toUpperCase() === couponCode.toUpperCase(),
    );

  const manualApplying =
    isBusy && pending?.type === 'apply' && pending.source === 'manual';

  const visibleOffers = showAllOffers ? coupons : coupons.slice(0, 6);
  const hasMoreOffers = coupons.length > 6;

  return (
    <section className="camp-section min-w-0 text-left !py-3">
      <div className="flex items-center gap-2">
        <TicketPercent className="h-4 w-4 text-[var(--dash-brand)]" />
        <h2 className="m-0 text-[13px] font-semibold text-[var(--dash-ink)]">
          Coupons &amp; offers
        </h2>
      </div>

      <div className="mt-2.5 flex gap-2">
        <input
          type="text"
          placeholder="Enter coupon code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit(trimmed, 'manual');
          }}
          disabled={isBusy}
          aria-label="Coupon code"
          className="min-w-0 flex-1 rounded-lg border border-[var(--dash-line)] bg-white px-3 py-2 text-sm font-semibold tracking-wide text-[var(--dash-ink)] uppercase outline-none transition placeholder:font-normal placeholder:tracking-normal placeholder:normal-case focus:border-[var(--dash-brand)] focus:ring-2 focus:ring-[var(--dash-brand)]/15 disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => submit(trimmed, 'manual')}
          disabled={isBusy || !trimmed || appliedCodes.has(trimmed.toUpperCase())}
          className="inline-flex min-w-[4.75rem] shrink-0 items-center justify-center rounded-lg bg-[var(--dash-brand)] px-3 py-2 text-[12px] font-semibold text-white transition hover:brightness-105 disabled:opacity-50"
        >
          {manualApplying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            'Apply'
          )}
        </button>
      </div>

      {applied.length > 0 ? (
        <div className="mt-2.5 flex flex-col gap-1.5">
          {applied.map((item) => (
            <AppliedCouponPill
              key={item.code}
              code={item.code}
              amountLabel={
                item.discount > 0 ? `−${formatMoney(item.discount)}` : 'Applied'
              }
              onRemove={
                item.autoApplied
                  ? undefined
                  : () => {
                      if (isBusy) return;
                      setPending({
                        type: 'remove',
                        code: item.code,
                        source: 'remove',
                      });
                      onRemove(item.code);
                    }
              }
              removing={isRemoving(item.code)}
            />
          ))}
        </div>
      ) : null}

      <div className="mt-3">
        <p className="m-0 mb-1.5 text-[10.5px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
          Available offers
          {coupons.length > 0 ? ` · ${coupons.length}` : ''}
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 rounded-lg border border-[var(--dash-line)] px-3 py-2 text-xs text-[var(--dash-muted)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Looking for offers…
          </div>
        ) : coupons.length === 0 ? (
          <p className="m-0 rounded-lg border border-dashed border-[var(--dash-line)] px-3 py-2 text-xs text-[var(--dash-muted)]">
            No coupons available for this order right now.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3">
              {visibleOffers.map((c) => {
                const couponCode = c.couponCode || '';
                const selected = appliedCodes.has(couponCode.toUpperCase());
                const applyingThis = isApplying(couponCode, 'offer');
                const discountLabel =
                  c.discountType === 'PERCENTAGE'
                    ? `${c.discountValue}% off`
                    : `${formatMoney(c.discountValue)} off`;

                return (
                  <div
                    key={c._id || c.id || couponCode}
                    className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 transition ${
                      selected
                        ? 'border-emerald-600/35 bg-emerald-50/40'
                        : 'border-[var(--dash-line)] bg-white'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12.5px] font-bold text-[var(--dash-ink)]">
                          {discountLabel}
                        </span>
                        <span className="rounded bg-[var(--dash-soft)] px-1 py-px font-mono text-[9.5px] font-bold tracking-wide text-[var(--dash-ink)] uppercase">
                          {couponCode}
                        </span>
                      </div>
                      <p className="m-0 mt-0.5 truncate text-[11px] text-[var(--dash-muted)]">
                        {c.couponName || couponCode}
                        {c.minSpend != null
                          ? ` · Min ${formatMoney(c.minSpend)}`
                          : ''}
                      </p>
                    </div>
                    {selected ? (
                      <span className="shrink-0 text-[10.5px] font-bold text-emerald-800">
                        Applied
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => submit(couponCode, 'offer')}
                        disabled={isBusy || !couponCode}
                        className="shrink-0 rounded-md bg-[var(--dash-ink)] px-2.5 py-1 text-[10.5px] font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                      >
                        {applyingThis ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          'Apply'
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {hasMoreOffers ? (
              <button
                type="button"
                onClick={() => setShowAllOffers((v) => !v)}
                className="mt-2 text-[12px] font-semibold text-[var(--dash-brand)] hover:underline"
              >
                {showAllOffers
                  ? 'Show fewer offers'
                  : `View all offers (${coupons.length})`}
              </button>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
